import React, { useMemo, useState } from "react";
import { X, Check, Loader2 } from "lucide-react";
import api, { SPORTS, SKILL_LEVELS, SPORT_POSITIONS, normalizeWa } from "../lib/api";
import { toast } from "sonner";

/**
 * Modal to edit user profile: name, sports, skill levels, WhatsApp number.
 * Props: user, onClose, onSaved(updatedUser)
 */
export default function EditProfileModal({ user, onClose, onSaved }) {
  const [name, setName] = useState(user?.name || "");
  const [whatsapp, setWhatsapp] = useState(user?.whatsapp || "");
  const [sports, setSports] = useState(user?.sports || []);
  const [skillLevels, setSkillLevels] = useState(user?.skill_levels || {});
  const [positions, setPositions] = useState(user?.positions || {});
  const [saving, setSaving] = useState(false);

  const skillOptions = useMemo(() => SKILL_LEVELS.filter((l) => l.id !== "all"), []);

  const toggleSport = (id) => {
    setSports((prev) => {
      const has = prev.includes(id);
      const next = has ? prev.filter((s) => s !== id) : [...prev, id];
      if (!has && !skillLevels[id]) {
        setSkillLevels((sl) => ({ ...sl, [id]: "intermediate" }));
      }
      return next;
    });
  };

  const setSkill = (sport, level) => setSkillLevels((sl) => ({ ...sl, [sport]: level }));
  const setPosition = (sport, pos) => setPositions((p) => ({ ...p, [sport]: pos }));

  const submit = async () => {
    if (!name.trim()) { toast.error("Nama tidak boleh kosong"); return; }
    setSaving(true);
    try {
      const cleanWa = normalizeWa(whatsapp);
      // Only persist positions for sports the user still has selected AND that support positions
      const cleanedPositions = Object.fromEntries(
        sports
          .filter((sid) => SPORT_POSITIONS[sid] && positions[sid])
          .map((sid) => [sid, positions[sid]])
      );
      const payload = {
        name: name.trim(),
        sports,
        skill_levels: Object.fromEntries(sports.map((s) => [s, skillLevels[s] || "intermediate"])),
        positions: cleanedPositions,
        whatsapp: cleanWa || null,
      };
      const { data } = await api.patch("/users/me", payload);
      toast.success("Profil tersimpan");
      onSaved && onSaved(data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal simpan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md bg-card border border-border rounded-t-3xl sm:rounded-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="edit-profile-modal">
        <div className="sticky top-0 bg-card flex items-center justify-between px-5 pt-5 pb-3 z-10 border-b border-border">
          <div>
            <div className="font-heading text-lg font-black tracking-tight">Edit Profil</div>
            <div className="text-xs text-muted-foreground">Update info kamu</div>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center" data-testid="edit-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-5">
          {/* Name */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Nama</label>
            <input
              data-testid="edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nama lengkap"
              className="mt-2 w-full bg-secondary border border-border rounded-2xl px-4 py-3.5 focus:outline-none focus:border-primary"
            />
          </div>

          {/* WhatsApp */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Nomor WhatsApp (optional)</label>
            <div className="mt-2 flex items-center gap-2 bg-secondary border border-border rounded-2xl pl-4 pr-2 py-1.5">
              <span className="text-sm font-bold text-muted-foreground shrink-0">+62</span>
              <input
                data-testid="edit-whatsapp"
                value={whatsapp.replace(/^62/, "")}
                onChange={(e) => setWhatsapp(e.target.value.replace(/\D/g, ""))}
                placeholder="81234567890"
                inputMode="numeric"
                className="flex-1 bg-transparent py-2 focus:outline-none"
              />
            </div>
            <div className="mt-1 text-[11px] text-muted-foreground">Biar pemain lain bisa chat WA langsung. Privasi: nomor cuma muncul ke pemain match yang sama.</div>
          </div>

          {/* Sports */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Olahraga</label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {SPORTS.map((s) => {
                const active = sports.includes(s.id);
                return (
                  <button
                    key={s.id}
                    data-testid={`edit-sport-${s.id}`}
                    onClick={() => toggleSport(s.id)}
                    className={`relative flex items-center gap-3 px-3 py-3 rounded-2xl border text-left transition-all ${active ? "border-primary bg-primary/10" : "border-border bg-secondary"}`}
                  >
                    <span className="text-xl">{s.icon}</span>
                    <span className="font-bold text-sm">{s.label}</span>
                    {active && <Check className="absolute top-2 right-2 h-4 w-4 text-primary" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Skill (+ Position) per sport */}
          {sports.length > 0 && (
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Skill Level</label>
              <div className="mt-2 space-y-2">
                {sports.map((sid) => {
                  const s = SPORTS.find((x) => x.id === sid);
                  const posList = SPORT_POSITIONS[sid];
                  return (
                    <div key={sid} className="bg-secondary/60 border border-border rounded-2xl p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">{s?.icon}</span>
                        <span className="font-bold text-sm">{s?.label}</span>
                      </div>
                      <div className="flex gap-1.5">
                        {skillOptions.map((l) => (
                          <button
                            key={l.id}
                            data-testid={`edit-skill-${sid}-${l.id}`}
                            onClick={() => setSkill(sid, l.id)}
                            className={`flex-1 px-2 py-1.5 rounded-full text-[11px] font-bold ${skillLevels[sid] === l.id ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground border border-border"}`}
                          >
                            {l.label}
                          </button>
                        ))}
                      </div>
                      {posList && (
                        <>
                          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mt-3 mb-1.5">Posisi</div>
                          <div className="flex flex-wrap gap-1.5">
                            {posList.map((p) => (
                              <button
                                key={p.id}
                                data-testid={`edit-position-${sid}-${p.id}`}
                                onClick={() => setPosition(sid, p.id)}
                                className={`px-2.5 py-1.5 rounded-full text-[11px] font-bold inline-flex items-center gap-1.5 ${positions[sid] === p.id ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground border border-border"}`}
                              >
                                <span className="text-[10px] opacity-80">{p.short}</span>
                                <span>{p.label}</span>
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <button
            data-testid="edit-submit"
            onClick={submit}
            disabled={saving}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary text-primary-foreground font-bold glow-cyan disabled:opacity-50 active:scale-95"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {saving ? "Menyimpan..." : "Simpan"}
          </button>
        </div>
      </div>
    </div>
  );
}
